import { createApp } from "vue";
import { createWebHistory, createRouter } from "vue-router";
import { createPinia, storeToRefs } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";

import { useTabStore } from "@/store/tabStore";
import { useUserStore } from "@/store/userStore";

// styles
import "@fortawesome/fontawesome-free/css/all.min.css";
// import "@/assets/styles/tailwind.css";

// mouting point for the whole app
import App from "@/App.vue";

import Notifications from "@kyvg/vue3-notification";

import AlertPlugin from "./plugins/alert";
import GlobalLoading from "./components/Common/GlobalLoading.vue";

// layouts
import LayoutLogin from "@/layouts/LayoutLogin.vue";
import LayoutScm from "@/layouts/LayoutScm.vue";

// views for Auth layout
import Login from "@/views/auth/Login.vue";
import NotFound from "@/views/auth/NotFound.vue";

// views for SCM layout
import ScmHome from "@/views/SCM/ScmHome.vue";
import OrderList from "@/views/SCM/OrderList.vue";
import ShipmentList from "@/views/SCM/ShipmentList.vue";
import UndeliveredList from "@/views/SCM/UndeliveredList.vue";
import LateFeeList from "@/views/SCM/LateFeeList.vue";
import NCRList from "@/views/SCM/NCRList.vue";
import SM303 from "@/views/SMM/SM303.vue";
import Print from "@/views/POPUP/Print.vue";
import FList from "@/views/POPUP/FList.vue";

// routes
const routes = [
  {
    path: "/",
    redirect: "/auth/login",
    component: LayoutLogin,
    children: [
      {
        path: "/auth/login",
        name: "login",
        component: Login,
      },
    ],
  },
  {
    path: "/scm",
    redirect: "/scm/home",
    component: LayoutScm,
    children: [
      {
        path: "/scm/home",
        component: ScmHome,
        meta: { requiresAuth: true, isMain: true },
        name: "Home",
      },
      {
        path: "/scm/OrderList",
        component: OrderList,
        meta: { requiresAuth: true },
        name: "주문현황",
      },
      {
        path: "/scm/ShipmentList",
        component: ShipmentList,
        meta: { requiresAuth: true },
        name: "출고일보",
      },
      {
        path: "/scm/UndeliveredList",
        component: UndeliveredList,
        meta: { requiresAuth: true },
        name: "미납현황",
      },
      {
        path: "/scm/LateFeeList",
        component: LateFeeList,
        meta: { requiresAuth: true },
        name: "연체료현황",
      },
      {
        path: "/scm/NCRList",
        component: NCRList,
        meta: { requiresAuth: true },
        name: "NCR현황",
      },
      {
        path: "/smm/SM303",
        component: SM303,
        meta: { requiresAuth: true },
        name: "재고조회(영업사원용)",
      },
    ],
  },
  {
    path: "/POPUP/print",
    name: "Print",
    component: Print,
  },
  {
    path: "/POPUP/flist",
    name: "FList",
    component: FList,
  },
  {
    // 잘못된 주소인 경우
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: NotFound,
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

const app = createApp(App);
app.component("GlobalLoading", GlobalLoading);

// 전역 인스턴스를 만들어서 $loading에 붙이기
let loadingInstance = null;
app.config.globalProperties.$loading = () => {
  if (!loadingInstance) {
    const container = document.createElement("div");
    document.body.appendChild(container);

    const vm = createApp(GlobalLoading).mount(container);
    loadingInstance = vm;
  }

  loadingInstance.show();
};

app.config.globalProperties.$loadingDone = () => {
  if (loadingInstance) {
    loadingInstance.hide();
  }
};

window.$loading = app.config.globalProperties.$loading;
window.$loadingDone = app.config.globalProperties.$loadingDone;

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate); // 새로고침시 데이터 유지

// app.use(store);
app.use(router);
app.use(pinia);
app.use(AlertPlugin);
app.use(Notifications);
app.mount("#app");

router.beforeEach((to, from, next) => {
  // console.log("router.beforeEach to, from", to, from, next);

  const tabStore = useTabStore();
  const userStore = useUserStore();
  const { token } = storeToRefs(userStore);

  const isAuthenticated = token.value;
  // console.log("isAuthenticated", isAuthenticated);

  if (to.meta.requiresAuth && !isAuthenticated) {
    app.config.globalProperties.$alert({
      title: "Error",
      message: "세션이 만료되었거나, 로그인이 실패되었습니다.",
    });

    // 인증이 필요한 페이지인데 로그인 안됨
    next({ name: "login" }); // 로그인 페이지로 리다이렉트
  } else if (to.meta.requiresAuth && isAuthenticated) {
    // console.log("22");

    if (to.path !== "/auth/login") {
      // 새로고침 시에도 메인화면 탭에 추가
      const scmRoute = routes.find((r) => r.path === "/scm");
      const mainTab = scmRoute?.children?.find(
        (data) => data.meta?.isMain === true
      ) || { name: "Home", path: "/scm/home" };

      if (mainTab) {
        const exists = tabStore.tabs.find((tab) => tab.path === mainTab.path);
        if (!exists) {
          tabStore.addTab(mainTab);
        }
      }

      // 상단 메뉴 탭 생성
      const tabName = to.name || to.path;
      tabStore.addTab({
        name: tabName,
        path: to.path,
      });
    }

    next(); // 통과
  } else {
    // console.log("33");

    next(); // 통과
  }
});
